import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { isUndefined } from "lodash";
import isFunction from "lodash/isFunction";
import React, { useMemo, useContext, ReactNode } from "react";

export type SDKContext = AxiosInstance;

const SDKContext = React.createContext<SDKContext>(axios);

const { Provider } = SDKContext;

export interface ISDKRequestInterceptor {
    kind: "request";
    onFullfilled: (
        value: AxiosRequestConfig,
        instance: AxiosInstance,
    ) => AxiosRequestConfig | Promise<AxiosRequestConfig>;
    onRejected?: (error: any, instance: AxiosInstance) => any;
}
export interface ISDKResponseInterceptor {
    kind: "response";
    onFullfilled: (
        value: AxiosResponse<any>,
        instance: AxiosInstance,
    ) => AxiosResponse<any> | Promise<AxiosResponse<any>>;
    onRejected?: (error: AxiosError, instance: AxiosInstance) => any;
}

function isSDKRequestInterceptor(
    interceptor: ISDKRequestInterceptor | ISDKResponseInterceptor,
): interceptor is ISDKRequestInterceptor {
    return interceptor.kind === "request";
}

function isSDKResponseInterceptor(
    interceptor: ISDKRequestInterceptor | ISDKResponseInterceptor,
): interceptor is ISDKResponseInterceptor {
    return interceptor.kind === "response";
}

type SDKProviderProps = {
    interceptors?: (ISDKRequestInterceptor | ISDKResponseInterceptor)[];
    baseURL: string;
    children: ReactNode | ((ctx: SDKContext) => ReactNode);
};

export function SDKProvider({ baseURL, children, interceptors = [] }: SDKProviderProps) {
    const api = useMemo(() => {
        const instance = axios.create({
            baseURL,
        });

        interceptors.forEach(interceptor => {
            if (isSDKRequestInterceptor(interceptor)) {
                const { onFullfilled, onRejected } = interceptor;
                const handleFullfilled = (request: AxiosRequestConfig) => onFullfilled(request, instance);
                const handleRejected = !isUndefined(onRejected)
                    ? (error: any) => onRejected(error, instance)
                    : undefined;
                instance.interceptors.request.use(handleFullfilled, handleRejected);
            } else if (isSDKResponseInterceptor(interceptor)) {
                const { onFullfilled, onRejected } = interceptor;
                const handleFullfilled = (response: AxiosResponse) => onFullfilled(response, instance);
                const handleRejected = !isUndefined(onRejected)
                    ? (error: any) => onRejected(error, instance)
                    : undefined;
                instance.interceptors.response.use(handleFullfilled, handleRejected);
            }
        });

        return instance;
    }, [baseURL]);

    const queryClient = useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        // See diagram https://bs-uploads.toptal.io/blackfish-uploads/uploaded_file/file/167418/image-1579535436801-78c658f57da8a1b95d58fb2da9fa35a5.png
                        cacheTime: 90 * 60 * 1000, // Keeps data in cache during 1h30min
                        staleTime: 1000, // Avoids to emit a new request during 1s
                        retry: 1, // Do not retry more than 1 time
                        refetchOnWindowFocus: false,
                    },
                },
            }),
        [],
    );

    return (
        <QueryClientProvider client={queryClient}>
            <Provider value={api}>{children && isFunction(children) ? children(api) : children}</Provider>
        </QueryClientProvider>
    );
}

export const useApi = () => useContext(SDKContext);

export default SDKContext;
